// The Format bar (#344): 8 buttons that float above the selected text in the message box and the edit box. A button is
// lit while the selection has its marks. The rule is shared/src/formatSelection.ts; the keys are in ../formatKeys.ts.
//
// Put this inside the "fl-field" (position: relative) that wraps the textarea and its FormatLayer, before the textarea.
// It draws nothing until the box has focus and part of its text is selected. Its buttons never take focus from the
// box, so the selection stays; they work with the keyboard too (Shift+Tab from the box).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { activeFormats, FORMAT_ITEMS, type FormatItem } from "@bloom/shared";
import { applyFormat, formatKeyLabel, isApple } from "../formatKeys.ts";
import { isTouch } from "../mobile.ts";
import { EyeOffIcon } from "./Attachments.tsx";

const NAMES: Record<FormatItem, string> = {
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  strike: "Strikethrough",
  code: "Code",
  spoiler: "Spoiler",
  quote: "Quote",
  codeblock: "Code block",
};

const KEY_NAMES: Record<string, string> = { b: "B", i: "I", u: "U", x: "X", e: "E" };

function Glyph({ item }: { item: FormatItem }): ReactNode {
  switch (item) {
    case "bold":
      return <b>B</b>;
    case "italic":
      return <i>I</i>;
    case "underline":
      return <u>U</u>;
    case "strike":
      return <s>S</s>;
    case "code":
      return <span className="format-mono">&lt;/&gt;</span>;
    case "spoiler":
      return <EyeOffIcon size={16} />;
    case "quote":
      return <span className="format-quote">&ldquo;</span>;
    case "codeblock":
      return <span className="format-mono">{"{ }"}</span>;
  }
}

/** Where the drawn character at `offset` is, from the FormatLayer's text (it holds the box's text, character for character). */
function rectAt(layer: Element | null | undefined, offset: number): DOMRect | null {
  if (!layer || typeof document.createRange !== "function") return null;
  try {
    const walker = document.createTreeWalker(layer, NodeFilter.SHOW_TEXT);
    let seen = 0;
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      const len = n.nodeValue?.length ?? 0;
      if (offset < seen + len) {
        const range = document.createRange();
        const at = offset - seen;
        range.setStart(n, at);
        range.setEnd(n, Math.min(at + 1, len));
        const rects = range.getClientRects();
        const r = rects[0] ?? range.getBoundingClientRect();
        return r && (r.width || r.height) ? r : null;
      }
      seen += len;
    }
  } catch {
    // no layout here
  }
  return null;
}

interface Props {
  area: RefObject<HTMLTextAreaElement | null>;
  /** The box's text (the bar redraws and re-lights when it changes). */
  text: string;
  /** Sets the box's whole text, for a browser that cannot edit the box itself. */
  setValue: (value: string) => void;
}

export function FormatBar({ area, text, setValue }: Props) {
  const [sel, setSel] = useState<{ start: number; end: number } | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const bar = useRef<HTMLDivElement>(null);

  // Whether part of the box's text is selected, and where. The box tells us in several ways, and none is enough alone.
  useEffect(() => {
    const ta = area.current;
    if (!ta) return;
    const read = () => {
      const focused = document.activeElement === ta || !!bar.current?.contains(document.activeElement);
      const { selectionStart: a, selectionEnd: b } = ta;
      const next = focused && a !== b ? { start: a, end: b } : null;
      setSel((prev) => (prev && next && prev.start === next.start && prev.end === next.end ? prev : prev || next ? next : prev));
    };
    const blur = (e: FocusEvent) => {
      if (!bar.current?.contains(e.relatedTarget as Node | null)) setSel(null);
    };
    const events = ["select", "keyup", "mouseup", "touchend", "input", "focus"] as const;
    for (const ev of events) ta.addEventListener(ev, read);
    ta.addEventListener("blur", blur);
    document.addEventListener("selectionchange", read);
    return () => {
      for (const ev of events) ta.removeEventListener(ev, read);
      ta.removeEventListener("blur", blur);
      document.removeEventListener("selectionchange", read);
    };
  }, [area]);

  // The bar sits above the start of the selection (below it on a phone when there is room, as the system's own menu
  // is above), inside the screen, and follows the box as it scrolls or resizes.
  useLayoutEffect(() => {
    const ta = area.current;
    const field = ta?.parentElement;
    if (!sel || !ta || !field || !bar.current) {
      setPos(null);
      return;
    }
    const place = () => {
      const f = field.getBoundingClientRect();
      const r = rectAt(field.querySelector(".fl-layer"), sel.start);
      const w = bar.current?.offsetWidth || 280;
      const h = bar.current?.offsetHeight || 34;
      const x = Math.min(Math.max(r ? r.left : f.left, 8), Math.max(8, window.innerWidth - w - 8));
      const top = r ? r.top : f.top;
      const bottom = r ? r.bottom : f.bottom;
      const screenH = window.visualViewport?.height ?? window.innerHeight;
      const roomAbove = top - h - 6 >= 4;
      const roomBelow = bottom + h + 6 <= screenH - 4;
      // On a phone the system's own menu is above the selection, so the bar goes below it when there is room.
      const below = isTouch() ? roomBelow : !roomAbove && roomBelow;
      const y = below ? bottom + 6 : top - h - 6;
      const next = { left: Math.round(x - f.left), top: Math.round(y - f.top) };
      setPos((p) => (p && p.left === next.left && p.top === next.top ? p : next));
    };
    place();
    ta.addEventListener("scroll", place, { passive: true });
    window.addEventListener("resize", place);
    return () => {
      ta.removeEventListener("scroll", place);
      window.removeEventListener("resize", place);
    };
  }, [sel, text, area]);

  if (!sel) return null;
  const on = activeFormats(text, sel.start, sel.end);
  const apple = isApple();

  return (
    <div
      ref={bar}
      className="format-bar"
      role="toolbar"
      aria-label="Format"
      style={pos ? { left: pos.left, top: pos.top } : { visibility: "hidden" }}
      // The box keeps its focus and its selection while a button is pressed.
      onMouseDown={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        if (e.pointerType !== "mouse") e.preventDefault();
      }}
    >
      {FORMAT_ITEMS.map((item) => {
        const key = formatKeyLabel(item);
        const lit = on.includes(item);
        return (
          <button
            key={item}
            type="button"
            className={`format-btn format-${item}${lit ? " on" : ""}`}
            aria-label={NAMES[item]}
            aria-pressed={lit}
            aria-keyshortcuts={key ? `${apple ? "Meta" : "Control"}+${item === "strike" ? "Shift+" : ""}${KEY_NAMES[key.slice(-1).toLowerCase()] ?? ""}` : undefined}
            title={key ? `${NAMES[item]} (${key})` : NAMES[item]}
            onClick={() => {
              const ta = area.current;
              if (!ta) return;
              applyFormat(ta, item, setValue);
            }}
          >
            <Glyph item={item} />
          </button>
        );
      })}
    </div>
  );
}
